import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  getAccountVendors,
  getAccountPaymentMadeById,
  createAccountPaymentMade,
  updateAccountPaymentMade,
} from '../core/_requests';
import SearchableSelect from '../../../shared/components/SearchableSelect'

export default function AccountsNewPaymentMade() {
  const navigate = useNavigate();
  const { id } = useParams();
  const paymentId = id ? Number(id) : null;
  const isEditing = paymentId !== null && !Number.isNaN(paymentId);

  const [saving, setSaving] = useState(false);
  const [loadingInitial, setLoadingInitial] = useState(false);

  // Vendor dropdown data
  const [vendors, setVendors] = useState<any[]>([]);

  // Form state
  const [vendorId, setVendorId] = useState('');
  const [paymentNumber, setPaymentNumber] = useState('');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [paymentMode, setPaymentMode] = useState('Bank Transfer');
  const [billNumber, setBillNumber] = useState('');
  const [paidThrough, setPaidThrough] = useState('Petty Cash');
  const [amount, setAmount] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    getAccountVendors()
      .then((data) => setVendors(data || []))
      .catch((err) => console.error('Failed to load vendors:', err));
  }, []);

  useEffect(() => {
    if (!isEditing || paymentId == null) return;
    setLoadingInitial(true);
    getAccountPaymentMadeById(paymentId)
      .then((data: any) => {
        if (data) {
          setVendorId(data.vendorId ? String(data.vendorId) : '');
          setPaymentNumber(data.paymentNumber || '');
          setReferenceNumber(data.referenceNumber || '');
          setPaymentDate(data.paymentDate || new Date().toISOString().split('T')[0]);
          setPaymentMode(data.paymentMode || 'Bank Transfer');
          setBillNumber(data.billNumber || '');
          setPaidThrough(data.paidThrough || 'Petty Cash');
          setAmount(data.amount !== undefined ? String(data.amount) : '');
          setNotes(data.notes || '');
        }
      })
      .catch((err) => console.error('Failed to load payment made:', err))
      .finally(() => setLoadingInitial(false));
  }, [isEditing, paymentId]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!vendorId || !paymentNumber || !amount || !paymentDate) {
      alert('Please fill in Vendor, Payment Number, Amount, and Payment Date.');
      return;
    }

    setSaving(true);
    const selectedVendor = vendors.find((v) => v.id === Number(vendorId));

    const payload = {
      vendorId: Number(vendorId),
      vendorName: selectedVendor ? (selectedVendor.displayName || selectedVendor.companyName || selectedVendor.name) : null,
      paymentNumber,
      referenceNumber: referenceNumber || null,
      paymentDate,
      paymentMode,
      billNumber: billNumber || null,
      paidThrough,
      amount: Number(amount) || 0,
      notes: notes || null,
    };

    try {
      if (isEditing && paymentId) {
        await updateAccountPaymentMade(paymentId, payload);
      } else {
        await createAccountPaymentMade(payload);
      }
      navigate('/accounts/purchase/payments-made');
    } catch (err: any) {
      console.error('Save failed:', err);
      alert('Failed to save payment');
    } finally {
      setSaving(false);
    }
  };

  const inputClass =
    'w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-700 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all';

  if (loadingInitial) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-slate-50">
        <p className="text-slate-500 font-medium">Loading Payment Details...</p>
      </div>
    );
  }

  return (
    <>
      <div className="flex h-screen w-full overflow-hidden bg-slate-50">
        
        <div id="sidebar-overlay" className="fixed inset-0 bg-black/50 z-40 hidden md:hidden"></div>
        <main className="flex-1 flex flex-col h-full overflow-hidden relative">
          
          <div className="flex-1 overflow-y-auto overflow-x-hidden p-4 md:p-6" id="main-scroll">
            <div className="max-w-4xl mx-auto pb-20">
              <div className="mb-6">
                <div className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1">
                  Accounts / Purchase / Payments Made
                </div>
                <h1 className="text-2xl font-bold text-slate-900 leading-tight">
                  {isEditing ? 'Edit Payment' : 'Record Payment'}
                </h1>
                <p className="text-sm text-slate-500">
                  {isEditing ? 'Update vendor payment details' : 'Record a new payment to a vendor'}
                </p>
              </div>

              <form onSubmit={handleSave} className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 mb-6">
                <div className="mb-5">
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">
                    Vendor Name <span className="text-red-500">*</span>
                  </label>
                  <SearchableSelect value={vendorId} onChange={(e) => setVendorId(e.target.value)} required className={inputClass}>
                    <option value="">Select a vendor...</option>
                    {vendors.map((v) => (
                      <option key={v.id} value={v.id}>
                        {v.displayName}
                      </option>
                    ))}
                  </SearchableSelect>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="mb-5">
                    <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">
                      Amount <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      required
                      placeholder="0.00"
                      className={inputClass}
                    />
                  </div>
                  <div className="mb-5">
                    <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">
                      Payment Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      value={paymentDate}
                      onChange={(e) => setPaymentDate(e.target.value)}
                      required
                      className={inputClass}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="mb-5">
                    <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">
                      Payment# <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={paymentNumber}
                      onChange={(e) => setPaymentNumber(e.target.value)}
                      required
                      placeholder="PAY-0001"
                      className={inputClass}
                    />
                  </div>
                  <div className="mb-5">
                    <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">
                      Reference#
                    </label>
                    <input
                      type="text"
                      value={referenceNumber}
                      onChange={(e) => setReferenceNumber(e.target.value)}
                      placeholder="Check or transaction ref"
                      className={inputClass}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="mb-5">
                    <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">
                      Payment Mode
                    </label>
                    <SearchableSelect value={paymentMode} onChange={(e) => setPaymentMode(e.target.value)} className={inputClass}>
                      <option value="Bank Transfer">Bank Transfer</option>
                      <option value="Credit Card">Credit Card</option>
                      <option value="Cash">Cash</option>
                      <option value="Check">Check</option>
                    </SearchableSelect>
                  </div>
                  <div className="mb-5">
                    <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">
                      Paid Through
                    </label>
                    <SearchableSelect value={paidThrough} onChange={(e) => setPaidThrough(e.target.value)} className={inputClass}>
                      <option value="Petty Cash">Petty Cash</option>
                      <option value="Operating Account">Operating Account</option>
                      <option value="Payroll Account">Payroll Account</option>
                    </SearchableSelect>
                  </div>
                </div>

                <div className="mb-5">
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">
                    Apply to Bill Number (Optional)
                  </label>
                  <input
                    type="text"
                    value={billNumber}
                    onChange={(e) => setBillNumber(e.target.value)}
                    placeholder="e.g. BILL-0010"
                    className={`${inputClass} ${isEditing ? 'bg-slate-50 opacity-70 cursor-not-allowed' : ''}`}
                    disabled={isEditing}
                  />
                </div>

                <hr className="my-6 border-slate-100" />

                <div className="mb-5">
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">
                    Notes
                  </label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={3}
                    placeholder="Internal notes about this payment"
                    className={`${inputClass} resize-none`}
                  ></textarea>
                </div>

                <div className="pt-6 mt-6 border-t border-slate-100 flex items-center gap-3">
                  <button
                    type="submit"
                    disabled={saving}
                    className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-bold shadow-sm hover:shadow transition-all disabled:opacity-50"
                  >
                    {saving ? 'Saving...' : isEditing ? 'Update Payment' : 'Save Payment'}
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate(-1)}
                    className="px-6 py-2.5 text-slate-500 hover:text-slate-700 text-sm font-bold transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
            
          </div>
        </main>
      </div>
      
    </>
  );
}

